import React, { useEffect, useState } from 'react';
import './ListingModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronLeft, faChevronRight, faCamera, faPrint, faShareAlt, faEdit, faTrash, faStethoscope, faPill, faSyringe } from '@fortawesome/free-solid-svg-icons';
import {axiosInstance} from './helper/axiosConfig';
import { Link, useLocation } from 'react-router-dom'; // Import Link and useLocation for navigatio
import prescriptionImg from '../assets/img/prescription-dummy.png'; // Import the logo

const getRandomColor = () => {
  const colors = ['#f56565', '#68d391', '#63b3ed', '#ed8936', '#f6ad55', '#9f7aea', '#48bb78', '#4299e1'];
  return colors[Math.floor(Math.random() * colors.length)];
};
// Utility function to get the first letter of the patient's name
const getAvatarLetter = (name) => {
  return name ? name.charAt(0).toUpperCase() : '?';
};


function ListingModal({ onClose, searchTerm, activeTab }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [patientHistory, setPatientHistory] = useState([]);
  const [medicineInfo, setMedicineInfo] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [length, setLength] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (searchTerm || activeTab) {
      if(activeTab == 'Prescription'){
        fetchFilteredData();
      } else if (activeTab == 'Patient'){
        fetchPatientHistory();
      } else if (activeTab == 'Medicine') {
        fetchMedicineInfo();
      }

    }
  }, [searchTerm, activeTab, currentPage]);

  const fetchFilteredData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/prescription-all/`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });

      const fetchedPrescriptions = response.data.results.map((prescription) => {
        const createdAt = new Date(prescription.created_at);
        return {
          ...prescription,
          date: `${createdAt.toLocaleDateString()} ${createdAt.toLocaleTimeString()}`,
        };
      });

      setPrescriptions(fetchedPrescriptions);
      setLength(response.data.total)
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
    } catch (error) {
      console.error('Error fetching filtered data:', error);
    } finally {
      setIsLoading(false);
    }
  };


  // Fetch patient history data with pagination and filtering
  const fetchMedicineInfo = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/medicine-all/`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });
      setMedicineInfo(response.data.results); // Assuming the API returns a `results` array
      setLength(response.data.total)
      setTotalPages(Math.ceil(response.data.total / itemsPerPage)); // Assuming the API returns a `total` count of patients
    } catch (error) {
      console.error('Error fetching patient history:', error);
      setError('Failed to fetch patient history.');
    } finally {
      setIsLoading(false); // Stop the loading state
    }
  };


  // Fetch patient history data with pagination and filtering
  const fetchPatientHistory = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/patient-all/`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });
      setPatientHistory(response.data.results); // Assuming the API returns a `results` array
      setLength(response.data.total)
      setTotalPages(Math.ceil(response.data.total / itemsPerPage)); // Assuming the API returns a `total` count of patients
    } catch (error) {
      console.error('Error fetching patient history:', error);
      setError('Failed to fetch patient history.');
    } finally {
      setIsLoading(false); // Stop the loading state
    }
  };


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>X</button>
        <div className="prescriptions-header">
          <h1>{activeTab}s ({length})</h1>
        </div>
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <>



          {activeTab === 'Prescription' && (
            <div className="prescriptions-section">
                {/* Prescriptions List */}
                {prescriptions && prescriptions.length > 0 ? (
                      prescriptions.map((prescription, index) => (
                          <div key={index} className="prescription-card">
                          <div className="prescription-image">
                          <Link to={`/prescription-preview/${prescription.id}`}><img src={prescriptionImg} alt="Prescription" /></Link>
                          </div>
                          <div className="prescription-details">
                              <p className="prescription-id">#{prescription.id.toString().padStart(6, '0')}</p>
                              <>
                                {/* Chief Complaint */}
                                {prescription.chief_complaint && (
                                  <p className="prescription-s-d">
                                    <span>Chief Complaint:</span> {prescription.chief_complaint}
                                  </p>
                                )}

                                {/* Doctor */}
                                {prescription.doctor && prescription.doctor.full_name && (
                                  <p className="prescription-s-d">
                                    <span>Doctor:</span> {prescription.doctor.full_name}
                                  </p>
                                )}

                                {/* Patient */}
                                {prescription.patient && prescription.patient.name && (
                                  <p className="prescription-s-d">
                                    <span>Patient:</span> {prescription.patient.name}
                                  </p>
                                )}

                                {prescription && (prescription.height > 0 || prescription.weight > 0) && (
                                  <p className="prescription-s-d">
                                    {prescription.height > 0 && <span>Height:</span>}
                                    {prescription.height > 0 && `${prescription.height} Feet, `}
                                    {prescription.weight > 0 && <span>Weight:</span>}
                                    {prescription.weight > 0 && `${prescription.weight} KG`}
                                  </p>
                                )}

                                {prescription?.relevant_history && (
                                  prescription.relevant_history.includes(',') ? (
                                    // Show bullets if there are commas
                                    <>
                                      <span>History:</span>
                                      {prescription.relevant_history.split(',').map((item, index) => (
                                        <div key={index} className='prescription-item-content'>
                                          * {item.trim()}
                                        </div>
                                      ))}
                                    </>
                                  ) : (
                                    // Show without bullets if there's no comma
                                    <>
                                      <span>History:</span> {prescription.relevant_history.trim()}
                                    </>
                                  )
                                )}
                              </>
                              <div className='prescription-btn' style={{ paddingTop: '10px' }}>
                              <button className="btn print-btn">
                              <Link to={`/prescription-preview/${prescription.id}`}><FontAwesomeIcon icon={faPrint} /> Print</Link>
                              </button>
                              <button className="btn share-btn">
                              <Link to={`/prescription-preview/${prescription.id}`}><FontAwesomeIcon icon={faShareAlt} /> Share</Link>
                              </button>
                              </div>
                          </div>
                          <div className="prescription-actions">
                            <p>
                              {prescription.created_at
                                ? `${new Date(prescription.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long', // or 'short' for shorter month names
                                    day: 'numeric',
                                  })} ${new Date(prescription.created_at).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                  })}`
                                : "N/A"}
                            </p>
                          </div>
                          </div>
                      ))

                  ) : (
                  <p>No prescriptions found.</p>
                  )}
            </div>
          )}

          {activeTab === 'Medicine' && (
            <div className="prescriptions-section">
                {/* medicineInfo List */}
                {medicineInfo && medicineInfo.length > 0 ? (
                      medicineInfo.map((medicine, index) => (
                          <div key={index} className="prescription-card">
                          <div className="medicine-icon">
                            <FontAwesomeIcon
                              icon={faStethoscope}
                              className="picker-icon"
                            />
                          </div>
                          <div className="prescription-details">
                              <h4 className="prescription-id">{medicine.name}, {medicine.doase_form}, {medicine.strength} </h4>
                              <p><span>Genric:</span> {medicine.genric_name}</p>
                              <p><span>Price:</span> {medicine.unit_price}</p>
                          </div>
                          </div>
                      ))

                  ) : (
                  <p>No medicine found.</p>
                  )}
            </div>
          )}

          {activeTab === 'Patient' && (
          <>
            {patientHistory && patientHistory.length > 0 ? (
                <>
                <table className="patient-history-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Marital Status</th>
                    <th>Address</th>
                    <th>&nbsp;</th>
                  {/*  <th>&nbsp;</th>*/}
                  </tr>
                </thead>
                <tbody>
                  {patientHistory.map((patient) => (
                    <tr key={patient.id}>
                      <td>
                        {/* Avatar circle
                        // <div
                        //   className="avatar-circle"
                        //   style={{ backgroundColor: getRandomColor() }}
                        // >
                        //   <span className="initials">{getAvatarLetter(patient.name)}</span>
                        // </div>
                        */}
                        <div className="avatar-circle" style={{ backgroundColor: !patient.profile_pic ? getRandomColor() : 'transparent' }}>
                          {patient.profile_pic ? (
                            <img
                                  src={`${process.env.REACT_APP_AXIOS_BASE_URL}${patient.profile_pic}`} // Concatenating the base URL and profile_pic
                                  alt={patient.full_name} className="avatar-circle"
                                // Ensure the profile-pic class is styled in your CSS
                                />

                          ) : (
                            <span className="initials">{getAvatarLetter(patient.name)}</span>
                          )}
                        </div>

                          {patient.name}
                      </td>
                      <td>{patient.age} {patient.age_type}</td>
                      <td>{patient.gender}</td>
                      <td>{patient.marital_status}</td>
                      <td>
                        {patient.address_district}
                        {patient.address_district && patient.address_thana ? ', ' : ''}
                        {patient.address_thana}
                      </td>
                      <td>
                        <Link to={`/patient-details/${patient.id}`}>
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="picker-icon"
                          />
                        </Link>
                      </td>
                      {/*
                        <td>{patient.has_prescription}
                            { !patient.has_prescription && ( // Call the function here
                                <a onClick={() => handleDeletePatient(patient.id)} className="trash-icon">
                                  <FontAwesomeIcon icon={faTrash} className="picker-icon" />
                                </a>
                            )}
                        </td>
                        */}
                    </tr>
                  ))}
                </tbody>
                </table>
                </>
              ) : (
              "No patient's found."
            )}
            </>
          )}


            {/* Pagination Controls */}
            <div className="pagination">
            {totalPages > itemsPerPage && (
              <>
              {currentPage > 1 && (
                <button onClick={() => handlePageChange(currentPage - 1)}>&lt;</button>
              )}
              {Array.from({ length: totalPages }, (_, index) => {
                if (index + 1 === currentPage || index + 1 === 1 || index + 1 === totalPages || Math.abs(currentPage - (index + 1)) <= 1) {
                  return (
                    <button
                      key={`page-${index + 1}`} // Use a unique key for each page button
                      onClick={() => handlePageChange(index + 1)}
                      className={currentPage === index + 1 ? 'active' : ''}
                    >
                      {index + 1}
                    </button>
                  );
                } else if (Math.abs(currentPage - (index + 1)) === 2) {
                  return <span key={`dots-${index}`} className="dots">...</span>; // Unique key for each "..."
                }
                return null;
              })}
              {currentPage < totalPages && (
                <button onClick={() => handlePageChange(currentPage + 1)}>&gt;</button>
              )}

              </>
            )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ListingModal;
